import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import withConfirmation from "hoc/withConfirmation";
import { notificationActions } from "actions";
import { textsAPI, textCategoriesAPI, requests } from "api";
import lh from "helpers/linkHandler";
import OutletWithDrawers from "global/components/router/OutletWithDrawers";
import Authorize from "hoc/Authorize";
import Category from "backend/components/category";
import cloneDeep from "lodash/cloneDeep";
import Button from "global/components/atomic/Button";
import withScreenReaderStatus from "hoc/withScreenReaderStatus";
import { useApiCallback } from "hooks";

const BUTTON_STYLE_PROPS = {
  size: "sm",
  shape: "lozenge",
  background: "outline-accent",
  lowercase: true
};

function ProjectTextsContainer({
  confirm,
  setScreenReaderStatus,
  renderLiveRegion
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { project, refresh, textRemovedRef } = useOutletContext() || {};

  const [categories, setCategories] = useState([]);
  const [texts, setTexts] = useState([]);

  useEffect(() => {
    if (project) {
      setCategories(project.relationships.textCategories.slice(0));
      setTexts(
        project.relationships.texts
          .filter(text => text.id !== textRemovedRef.current)
          .slice(0)
      );
    }
  }, [project, textRemovedRef]);

  const updateCategory = useApiCallback(textCategoriesAPI.update, {
    requestKey: requests.beTextCategoryUpdate,
    noTouch: true,
    notificationScope: "none"
  });

  const destroyCategory = useApiCallback(textCategoriesAPI.destroy, {
    requestKey: requests.beTextCategoryDestroy
  });

  const updateText = useApiCallback(textsAPI.update, {
    requestKey: requests.beTextUpdate,
    noTouch: true
  });

  const destroyText = useApiCallback(textsAPI.destroy, {
    requestKey: requests.beTextDestroy
  });

  const handleCategoryDestroy = category => {
    const heading = t("modals.delete_category");
    const message = t("modals.delete_category_body");
    confirm(heading, message, async () => {
      await destroyCategory(category.id);
      if (refresh) refresh();
    });
  };

  const handleTextDestroy = (text, onConfirmed) => {
    const heading = t("modals.delete_text");
    const message = t("modals.delete_text_body");
    confirm(heading, message, async () => {
      if (onConfirmed) onConfirmed();
      await destroyText(text.id);
      if (refresh) refresh();
      textRemovedRef.current = text.id;
      const notification = {
        level: 0,
        id: `TEXT_DESTROYED_${text.id}`,
        heading: t("notifications.text_delete"),
        body: t("notifications.text_delete_body", {
          title: text.attributes.titlePlaintext
        }),
        expiration: 5000
      };
      dispatch(notificationActions.addNotification(notification));
    });
  };

  const updateCategoryPosition = async (
    category,
    position,
    callback,
    announce
  ) => {
    // Optimistic update
    const newCategories = categories.filter(c => c.id !== category.id);
    newCategories.splice(position - 1, 0, category);
    setCategories(newCategories);

    const changes = { attributes: { position } };
    await updateCategory(category.id, changes);
    if (refresh) refresh();

    if (announce) {
      const announcement = t("actions.dnd.moved_to_position", {
        title: category.attributes.title,
        position
      });
      setScreenReaderStatus(announcement);
    }

    if (callback && typeof callback === "function") {
      callback();
    }
  };

  const updateTextCategoryAndPosition = async (
    text,
    category,
    position,
    callback,
    announce
  ) => {
    const catPayload = category
      ? { id: category.id, type: "categories" }
      : null;
    const changes = {
      relationships: { category: { data: catPayload } },
      attributes: { position }
    };

    // Optimistic update
    const newTexts = texts.filter(tx => tx.id !== text.id);
    const clone = cloneDeep(texts.find(tx => tx.id === text.id));
    clone.attributes = Object.assign(clone.attributes, changes.attributes);
    clone.relationships.category = changes.relationships.category.data;
    newTexts.splice(clone.attributes.position - 1, 0, clone);
    setTexts(newTexts);

    await updateText(text.id, changes);
    if (refresh) refresh();

    if (announce) {
      const announcement = t("actions.dnd.moved_to_category", {
        title: text.attributes.title,
        category: `${category?.attributes.title || "Uncategorized"}`,
        position
      });
      setScreenReaderStatus(announcement);
    }

    if (callback && typeof callback === "function") {
      callback();
    }
  };

  if (!project) return null;

  const callbacks = {
    destroyCategory: handleCategoryDestroy,
    destroyText: handleTextDestroy,
    updateCategoryPosition,
    updateTextCategoryAndPosition
  };

  const closeUrl = lh.link("backendProjectTexts", project.id);

  const drawerProps = [
    {
      context: "backend",
      lockScroll: "always",
      wide: true,
      lockScrollClickCloses: false,
      closeUrl
    },
    {
      context: "ingestion",
      lockScroll: "always",
      wide: true,
      lockScrollClickCloses: false,
      closeUrl
    }
  ];

  return (
    <Authorize
      entity={project}
      ability="manageTexts"
      failureNotification
      failureRedirect={lh.link("backendProject", project.id)}
    >
      <>
        <OutletWithDrawers
          drawerProps={drawerProps}
          context={{ project, refresh }}
        />

        <div className="entity-list__button-set-flex">
          <Button
            as={Link}
            to={lh.link("backendProjectTextsIngestionsNew", project.id)}
            label={t("texts.ingest_button_label")}
            preIcon="export24"
            {...BUTTON_STYLE_PROPS}
          />
          <Button
            as={Link}
            to={lh.link("backendProjectTextsCreate", project.id)}
            label={t("texts.create_button_label")}
            preIcon="copy24"
            {...BUTTON_STYLE_PROPS}
          />
          <Button
            as={Link}
            to={lh.link("backendProjectCategoriesNew", project.id)}
            label={t("texts.create_category_button_label")}
            preIcon="circlePlus24"
            {...BUTTON_STYLE_PROPS}
          />
        </div>

        <Category.List
          categories={categories}
          texts={texts}
          project={project}
          callbacks={callbacks}
          setScreenReaderStatus={setScreenReaderStatus}
        />
        {renderLiveRegion("alert")}
      </>
    </Authorize>
  );
}

ProjectTextsContainer.displayName = "Project.Texts";

export default withScreenReaderStatus(
  withConfirmation(ProjectTextsContainer),
  false
);
